import {ProductSearchHit} from '../../store/data/productSearchResult/interface';
import {IProductPriceObject} from '../product/IProduct';
import ProductSearchHitModel from 'coreClasses/search/ProductSearchHitModel';

/**
 * Represents SearchHitPriceModel
 * Price model for ProductSearchHit, compatible with AbstractPriceModel API
 */
class SearchHitPriceModel {
	private searchHitModel: ProductSearchHitModel;
	private hit: ProductSearchHit;

	/**
	 * @constructor
	 * @param {ProductSearchHitModel} searchHitModel - product search hit
	 */
	constructor(searchHitModel: ProductSearchHitModel) {
		this.searchHitModel = searchHitModel;
		this.hit = searchHitModel.productHit;
	}

	/**
	 * Returns is sales price book applied
	 * @returns {boolean} - is sales price book applied
	 */
	isSalesPriceBookApplied(): boolean {
		return this.getSales().pricebook.includes('Sales');
	}

	/**
	 * Check that product has price range on product variation group level
	 * @returns {boolean} - product has price range
	 */
	hasPriceRange(): boolean {
		const ranges = this.hit?.priceRanges || [];

		return ranges.some(e => e.maxPrice > e.minPrice);
	}

	/**
	 * Returns the sales product price
	 * @return {IProductPriceObject}
	 */
	getSales(): IProductPriceObject {
		return this.getMinPrice();
	}

	/**
	 * Returns the list product price
	 * @return {IProductPriceObject | null}
	 */
	getList(): IProductPriceObject | null {
		const minPrice = this.getMinPrice();
		const maxPrice = this.getMaxPrice();

		if (minPrice && maxPrice && minPrice.value !== maxPrice.value) {
			return maxPrice;
		}

		return null;
	}

	/**
	 * Returns the maximum price of the same variant used to retrieve minimal price
	 * @returns {IProductPriceObject} - the maximum price
	 */
	getMaxPrice() : IProductPriceObject {
		const variantsMaxPrice = this.searchHitModel.getVariants()?.sort((a, b) => {
			return b.getPriceModel().getMinPrice().value - a.getPriceModel().getMinPrice().value;
		})
			.pop()
			?.getPriceModel()
			.getMaxPrice();

		if (variantsMaxPrice?.value) {
			return variantsMaxPrice;
		} else if (this.hit.priceRanges?.length) {
			const maxPrice = this.hit.priceRanges.reduce((maxPrice: IProductPriceObject, priceRange) => {
				return (priceRange.maxPrice > maxPrice) ? priceRange.maxPrice : maxPrice;
			}, 0);

			return {
				currency: this.hit.currency,
				value: maxPrice,
				pricebook: this.hit.priceRanges
					.find((priceRange) => priceRange.maxPrice === maxPrice).pricebook
			};
		} else {
			return this.searchHitModel.getPrice();
		}
	}

	/**
	 * Returns the minimal price taking into consideration product promotions
	 * @return {IProductPriceObject} the minimal price
	 */
	getMinPrice(): IProductPriceObject {
		const currency = this.hit?.currency || '';
		const promotionalPrice = this.hit?.productPromotions?.reduce(
			(minPrice: number | null, promotion) => {
				const currentPromotionalPrice = promotion.promotionalPrice;

				if (typeof currentPromotionalPrice !== 'number') {
					return minPrice;
				}

				return minPrice === null || currentPromotionalPrice < minPrice
					? currentPromotionalPrice
					: minPrice;
			},
			null,
		);

		if (promotionalPrice) {
			return {
				currency,
				value: promotionalPrice,
				pricebook: 'promotional',
			};
		}

		return {
			currency,
			value: this.hit?.price ?? 0,
			pricebook: this.hit?.priceRanges?.find((priceRange) => priceRange.minPrice === this.hit?.price)?.pricebook,
		};
	}

	/**
	 * Returns the discount percent of sales price
	 * @return {number} the discount percent
	 */
	getDiscountPercent(): number {
		const minPrice = this.getMinPrice();
		const maxPrice = this.getMaxPrice();

		return ((maxPrice.value - minPrice.value) / maxPrice.value) * 100;
	}
}

export default SearchHitPriceModel;
